import {
  convertUnitFromTo,
  formatUnitFromTo,
  formatUnitToFit,
  getOptimalUnit,
  UNIT_MAP,
} from '@herit-fe/heritage-common';
import type {
  FormatUnitFromToOptions,
  FormatUnitToFitOptions,
  Optimizer,
  Unit,
} from '@herit-fe/heritage-common';

// heritage-common(순수 단위 포맷터) 표시 문자열 래퍼 — web·admin 공용 단위 표시 정책의 SSOT.
//
// 라이브러리 함수는 `FormattedNumberMeta`(display·value·direction…)를 반환하지만 화면은 보통 표시
// 문자열만 필요하다 → `.display` 만 꺼내는 얇은 래퍼. 옵션·제네릭을 그대로 위임하므로, 두 번째 인자
// 의미상수(`presets.ts`)의 `unit` 리터럴이 `U` 로 추론된다. 반올림·소수는 라이브러리 기본값을 따른다.

/**
 * `formatUnitFromTo` 의 `.display` 래퍼 — 단위를 **고정**해 변환·표시할 때(차트 축 단위 통일 등).
 */
export function formatUnitFromToDisplay<U extends string = Unit>(
  value: number | null | undefined,
  options?: FormatUnitFromToOptions<U>,
): string {
  return formatUnitFromTo(value, options).display;
}

/**
 * `formatUnitToFit` 의 `.display` 래퍼 — 값 크기에 맞춰 단위를 **자동 선택**해 표시할 때.
 */
export function formatUnitToFitDisplay<U extends string = Unit>(
  value: number | null | undefined,
  options?: FormatUnitToFitOptions<U>,
): string {
  return formatUnitToFit(value, options).display;
}

// ── 차트 단위 정책 ─────────────────────────────────────────────────────────────
// 축과 툴팁의 단위는 **분리**한다: 축은 여러 값에 공통인 단일 단위(tick 길이 최소화), 툴팁은 값별 자동 단위.
// viz 는 도메인 무지 — 이 헬퍼로 뽑은 단위/포맷터를 소비처가 축(tickFormatter·라벨)·툴팁에 주입한다.

/** 단위 preset(WATT·AREA 등) — `unit` 필수(unit-family 키). `optimalUnit`/`unitValueFormatter` 입력. */
type UnitPreset<U extends Unit> = FormatUnitToFitOptions<U> & { unit: U };

/**
 * 단위 심볼('Wh'·'kW'·'MWh'…) → magnitude 변환 옵션(`{ unit: family, from: symbol }`) | null. `UNIT_MAP` 을 스캔해
 * 그 심볼을 `symbols` 에 가진 family 를 찾는다 — **하드코딩 심볼↔family 매핑·배율(÷1000) 없이** BE 응답의 단위
 * 문자열 그대로 magnitude 여부를 판정. magnitude family 밖(V·A·Hz·%·분 등)은 null(정적 심볼로 표기). 반환 preset 의
 * `from` 으로 `optimalUnit`·`unitValueFormatter`·`formatUnitToFitDisplay` 가 base 가정 없이 원단위에서 변환한다.
 */
export function magnitudeUnitOf(symbol: string): UnitPreset<Unit> | null {
  if (!symbol) return null; // 무단위 — myriad family 의 base 심볼('')과 오매칭 방지(상태코드 등은 정적).
  for (const [family, spec] of Object.entries(UNIT_MAP)) {
    if ((spec as { symbols: readonly string[] }).symbols.includes(symbol)) {
      // family=UNIT_MAP 키(=Unit), from=원단위 심볼. heritage 가 심볼 리터럴 유니언을 export 안 해 `from` 국소 이스케이프.
      return { unit: family as Unit, from: symbol as never };
    }
  }
  return null;
}

/**
 * preset(WATT·AREA 등)의 표시 단위 심볼 — 기본 = base 단위(kW·m²), `unit` 지정 시 그 단위. 하드코딩('kW')
 * 대신 heritage 심볼을 뽑는다(값과 별개로 단위만 필요할 때 — 예: 표 컬럼 헤더에 단위 1회 표기).
 */
export function unitSymbol<U extends Unit = Unit>(preset: UnitPreset<U>, unit?: string): string {
  return convertUnitFromTo({ value: 0, unit: preset.unit, to: unit as never }).symbol;
}

/**
 * 값 배열에 공통으로 쓸 최적 단위 심볼 — heritage `getOptimalUnit` 위임(차트 축·표 컬럼 단위 통일).
 * default `optimizer='max'`(최댓값 기준 = 축 tick 길이 최소화). `'min'`=작은 값 보존, `'freq'`=최빈.
 */
export function optimalUnit<U extends Unit = Unit>(
  values: number[],
  preset: UnitPreset<U>,
  optimizer: Optimizer = 'max',
): string {
  // preset.from(원단위 심볼) 을 전달 — base 가정 없이 실제 입력 단위 기준으로 최적 단위 선정(미지정 시 baseIndex).
  // heritage 가 심볼 리터럴 유니언(SymbolOf)을 export 안 해 `from` 만 국소 이스케이프(values·unit 은 타입검사 보존).
  return getOptimalUnit({ values, unit: preset.unit, from: preset.from as never, optimizer });
}

/**
 * 고정 단위 값 포맷터 — base 단위 값을 `unit` 으로 변환한 **맨숫자**(콤마·0~2 소수 trim). 단위 심볼은 분리
 * (헤더/축 라벨이 1회 표기, 툴팁과도 분리). 한 축·한 컬럼을 단일 단위로 통일해 값마다 스케일이 갈리는 문제를 피한다.
 *
 * `unit` 은 보통 `optimalUnit` 으로 뽑는다: **차트 축**=`max`(tick 길이 최소화), **표 컬럼**=`min`(0 제외한
 * 최솟값 보존, 작은 값이 0으로 뭉개지는 것 방지 — 레거시 useConvertedUnit 정합). 소비처가 `optimalUnit →
 * unitValueFormatter` 를 조립한다(단위를 미리 굳히지 않음 — 차트 축은 가시 시리즈 변화마다 재선정).
 */
export function unitValueFormatter<U extends Unit = Unit>(
  preset: UnitPreset<U>,
  unit: string,
): (value: number) => string {
  return (value: number): string => {
    // heritage 가 `SymbolOf`(심볼 리터럴 유니언)를 export 안 해 `from`/`to` 만 국소 이스케이프 — value·unit 은 타입검사
    // 보존. preset.from(원단위) 기준 변환 → base 가정 없이 원단위 값(Wh 등)을 표시 단위로 정확히 변환한다.
    const converted = convertUnitFromTo({
      value,
      unit: preset.unit,
      from: preset.from as never,
      to: unit as never,
    }).value;
    return formatUnitToFitDisplay(converted, { minDecimals: 0, maxDecimals: 2 });
  };
}
